import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, CalendarClock, ArrowRight, ShieldCheck } from "lucide-react";
import { AuthShell, Field, inputClass } from "@/components/AuthShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const STATES = ["Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal"];

export default function Onboarding() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState({ full_name: "", date_of_birth: "", time_of_birth: "", birth_city: "", birth_state: "", birth_country: "India" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/birth-profile").then((r) => {
      if (r.data) setF((p) => ({ ...p, ...r.data }));
      else setF((p) => ({ ...p, full_name: user?.name || "" }));
    }).catch(() => {});
  }, [user]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.put("/birth-profile", f);
      toast.success("Birth details saved");
      nav("/welcome");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Your Birth Details" subtitle="Accurate birth details help us provide more personalized astrology guidance.">
      <form onSubmit={submit} className="space-y-4" data-testid="birth-details-form">
        <Field label="Full Name">
          <input data-testid="birth-fullname-input" required value={f.full_name} onChange={set("full_name")} className={inputClass} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date of Birth">
            <input data-testid="birth-dob-input" type="date" required value={f.date_of_birth} onChange={set("date_of_birth")} className={inputClass} />
          </Field>
          <Field label="Time of Birth">
            <input data-testid="birth-tob-input" type="time" required value={f.time_of_birth} onChange={set("time_of_birth")} className={inputClass} />
          </Field>
        </div>
        <Field label="Birth City">
          <input data-testid="birth-city-input" required value={f.birth_city} onChange={set("birth_city")} className={inputClass} placeholder="e.g. Mumbai" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Birth State">
            <select data-testid="birth-state-select" required value={f.birth_state} onChange={set("birth_state")} className={inputClass}>
              <option value="">Select state</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Birth Country">
            <input data-testid="birth-country-input" required value={f.birth_country} onChange={set("birth_country")} className={inputClass} />
          </Field>
        </div>
        <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-4 py-3">
          <ShieldCheck size={16} className="text-amber-300 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-400">Your birth details are stored securely and are never shared with other users.</p>
        </div>
        {error && <p data-testid="birth-error" className="text-sm text-red-400">{error}</p>}
        <button data-testid="birth-submit-button" type="submit" disabled={loading}
          className="btn-gold rounded-full w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? "Saving…" : "Continue to Subscription"} <ArrowRight size={18} />
        </button>
      </form>
    </AuthShell>
  );
}
